<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import posterImg from '@/assets/img/photos/movie.jpg'
import videoFile from '@/assets/media/movie.mp4'

// Fallback gallery images
import g8Img from '@/assets/img/photos/g8.jpg'
import g8Img2x from '@/assets/img/photos/g8@2x.jpg'
import g9Img from '@/assets/img/photos/g9.jpg'
import g9Img2x from '@/assets/img/photos/g9@2x.jpg'
import g10Img from '@/assets/img/photos/g10.jpg'
import g10Img2x from '@/assets/img/photos/g10@2x.jpg'

// Fallback left image
import about7Img from '@/assets/img/photos/about7.jpg'
import about7Img2x from '@/assets/img/photos/about7@2x.jpg'

const props = defineProps({
    works: {
        type: Array,
        default: () => []
    }
})

// Track if video has started playing
const isPlaying = ref(false)

// Get the featured (first) work
const featuredWork = computed(() => props.works[0] || null)



// Use featured work data or fallback
const poster = computed(() => featuredWork.value?.image || posterImg)
const videoSrc = computed(() => featuredWork.value?.videoUrl || videoFile)
const featuredDescription = computed(() => featuredWork.value?.description || 'A showcase of the stories we\'ve brought to life, ranging from corporate profiles and commercial campaigns to animation and documentary pieces. Every project reflects our commitment to creativity, clarity, and impactful visual storytelling.')

// Detect video type and extract IDs
const videoType = computed(() => {
    const url = videoSrc.value
    if (!url) return 'direct'
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
        return 'youtube'
    }
    if (url.includes('drive.google.com')) {
        return 'gdrive'
    }
    if (url.includes('onedrive.live.com') || url.includes('1drv.ms') || url.includes('sharepoint.com')) {
        return 'onedrive'
    }
    return 'direct'
})

// Extract YouTube video ID
const youtubeId = computed(() => {
    const url = videoSrc.value
    if (!url) return ''

    const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/
    const match = url.match(regex)
    return match ? match[1] : ''
})

// YouTube embed URL with autoplay
const youtubeEmbedUrl = computed(() => {
    return `https://www.youtube.com/embed/${youtubeId.value}?rel=0&modestbranding=1&autoplay=1`
})

// Convert Google Drive share link to embed link
const gdriveEmbedUrl = computed(() => {
    const url = videoSrc.value
    if (!url) return ''

    // Extract file ID from Google Drive share link
    // Format: https://drive.google.com/file/d/[FILE_ID]/view
    const match = url.match(/\/d\/([a-zA-Z0-9_-]+)/)
    if (match) {
        return `https://drive.google.com/file/d/${match[1]}/preview`
    }
    return ''
})

// Convert OneDrive share link to embed link
const onedriveEmbedUrl = computed(() => {
    const url = videoSrc.value
    if (!url) return ''

    // Extract file ID from OneDrive share link
    // Format 1: https://onedrive.live.com/?resid=[ID]&authkey=[KEY]
    // Format 2: https://1drv.ms/v/s![ID]
    // Format 3: https://smilepicture-my.sharepoint.com/:v:/g/personal/[path]/[ID]
    let embedUrl = url
    
    if (url.includes('sharepoint.com')) {
        // SharePoint format: extract the ID from the URL
        const match = url.match(/\/([a-zA-Z0-9_-]+)$/)
        if (match) {
            const fileId = match[1]
            embedUrl = url;
        }
    } else if (url.includes('onedrive.live.com')) {
        embedUrl = url.replace('guestinteractiontime=0&', '')
        embedUrl = embedUrl + (embedUrl.includes('?') ? '&' : '?') + 'em=2'
    } else if (url.includes('1drv.ms')) {
        const match = url.match(/s!([A-Za-z0-9_-]+)/)
        if (match) {
            embedUrl = `https://onedrive.live.com/embed?resid=${match[1]}&authkey=!AEzOfeSvbKwYrmQ&em=2`
        }
    }
    
    return embedUrl
})

// Play video function
const playVideo = () => {
    isPlaying.value = true
}

const g8 = g8Img
const g8_2x = g8Img2x
const g9 = g9Img
const g9_2x = g9Img2x
const g10 = g10Img
const g10_2x = g10Img2x

const about7 = about7Img
const about7_2x = about7Img2x



</script>

<template>
    <div v-if="works.length > 0">
        <!-- ===== VIDEO SECTION ===== -->
        <section class="wrapper bg-black">
            <div class="container pt-10 pb-12 pt-md-14 pb-md-16 text-center">
                <div class="row">
                    <div class="mx-auto text-white mb-6">
                        <h1 class="display-1 mb-3 text-white">
                            Some of <span style="color: #ffae0b;">Works</span>
                        </h1>
                        <p class="lead">A showcase of the stories we've brought to life, ranging from corporate profiles
                            and
                            commercial campaigns to animation and documentary pieces. Every project reflects our
                            commitment
                            to creativity, clarity, and impactful visual storytelling.

                        </p>
                    </div>

                    <!-- Video -->
                    <div class="video-wrapper">
                        <!-- YouTube Player -->
                        <div v-if="videoType === 'youtube'" class="player youtube-player">
                            <iframe v-if="isPlaying" :src="youtubeEmbedUrl" frameborder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowfullscreen></iframe>
                            <div v-else class="poster-overlay" @click="playVideo">
                                <img :src="poster" alt="Video poster" class="poster-image">
                                <div class="play-button">
                                    <svg viewBox="0 0 68 48" width="68" height="48">
                                        <path
                                            d="M66.52,7.74c-0.78-2.93-2.49-5.41-5.42-6.19C55.79,.13,34,0,34,0S12.21,.13,6.9,1.55 C3.97,2.33,2.27,4.81,1.48,7.74C0.06,13.05,0,24,0,24s0.06,10.95,1.48,16.26c0.78,2.93,2.49,5.41,5.42,6.19 C12.21,47.87,34,48,34,48s21.79-0.13,27.1-1.55c2.93-0.78,4.64-3.26,5.42-6.19C67.94,34.95,68,24,68,24S67.94,13.05,66.52,7.74z"
                                            fill-opacity="0.8"></path>
                                        <path d="M 45,24 27,14 27,34" fill="#fff"></path>
                                    </svg>
                                </div>
                            </div>
                        </div>

                        <!-- Google Drive Player -->
                        <div v-else-if="videoType === 'gdrive'" class="player gdrive-player">
                            <iframe v-if="isPlaying" :src="gdriveEmbedUrl" frameborder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowfullscreen></iframe>
                            <div v-else class="poster-overlay" @click="playVideo">
                                <img :src="poster" alt="Video poster" class="poster-image">
                                <div class="play-button">
                                    <svg viewBox="0 0 68 48" width="68" height="48">
                                        <path
                                            d="M66.52,7.74c-0.78-2.93-2.49-5.41-5.42-6.19C55.79,.13,34,0,34,0S12.21,.13,6.9,1.55 C3.97,2.33,2.27,4.81,1.48,7.74C0.06,13.05,0,24,0,24s0.06,10.95,1.48,16.26c0.78,2.93,2.49,5.41,5.42,6.19 C12.21,47.87,34,48,34,48s21.79-0.13,27.1-1.55c2.93-0.78,4.64-3.26,5.42-6.19C67.94,34.95,68,24,68,24S67.94,13.05,66.52,7.74z"
                                            fill-opacity="0.8"></path>
                                        <path d="M 45,24 27,14 27,34" fill="#fff"></path>
                                    </svg>
                                </div>
                            </div>
                        </div>

                        <!-- OneDrive Player -->
                        <div v-else-if="videoType === 'onedrive'" class="player onedrive-player">
                            <iframe v-if="isPlaying" :src="onedriveEmbedUrl" frameborder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowfullscreen></iframe>
                            <div v-else class="poster-overlay" @click="playVideo">
                                <img :src="poster" alt="Video poster" class="poster-image">
                                <div class="play-button">
                                    <svg viewBox="0 0 68 48" width="68" height="48">
                                        <path
                                            d="M66.52,7.74c-0.78-2.93-2.49-5.41-5.42-6.19C55.79,.13,34,0,34,0S12.21,.13,6.9,1.55 C3.97,2.33,2.27,4.81,1.48,7.74C0.06,13.05,0,24,0,24s0.06,10.95,1.48,16.26c0.78,2.93,2.49,5.41,5.42,6.19 C12.21,47.87,34,48,34,48s21.79-0.13,27.1-1.55c2.93-0.78,4.64-3.26,5.42-6.19C67.94,34.95,68,24,68,24S67.94,13.05,66.52,7.74z"
                                            fill-opacity="0.8"></path>
                                        <path d="M 45,24 27,14 27,34" fill="#fff"></path>
                                    </svg>
                                </div>
                            </div>
                        </div>

                        <!-- Direct Video Player -->
                        <video v-else :poster="poster" class="player" playsinline controls preload="none">
                            <source :src="videoSrc" type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>
                    </div>
                </div>
            </div>
        </section>

        <!-- ===== IMAGE GALLERY ===== -->
        <section class="wrapper bg-black">
            <div class="container">
                <div class="row gy-5 align-items-stretch pb-10">
                    <div v-if="works.length >= 2" class="row gy-5 align-items-stretch pb-10">
                        <!-- KIRI (Featured Work) -->
                        <div class="col-lg-6 d-flex">
                            <div class="services-card w-100 h-100">
                                <div class="services-image h-100">
                                    <img :src="works[1]?.image || about7" :srcset="works[1]?.image ? '' : about7_2x"
                                        :alt="works[1]?.title || 'Work'">
                                </div>
                                <div class="team-info">
                                    <div class="team-content">
                                        <span class="team-name">{{ works[1]?.title || 'Featured Project' }}</span>
                                        <router-link v-if="works[1]?.slug"
                                            :to="{ name: 'Works Detail', params: { slug: works[1].slug } }"
                                            class="view-video-btn">
                                            View Detail
                                        </router-link>
                                        <router-link v-else :to="`/works/${works[1].slug}`" class="view-video-btn">
                                            View Detail
                                        </router-link>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- KANAN (Rest of works from works[1] onwards) -->
                        <div class="col-lg-6">
                            <div class="row gy-5 h-100">
                                <template v-for="(work, index) in works.slice(2, 5)" :key="work.id">
                                    <div :class="index === 0 ? 'col-md-12' : 'col-md-6'">
                                        <div class="services-card">
                                            <div class="services-image" :class="index === 0 ? '' : 'ratio ratio-1x1'">
                                                <img :src="work.image" :alt="work.title">
                                            </div>
                                            <div class="team-info">
                                                <div class="team-content">
                                                    <span class="team-name">{{ work.title }}</span>
                                                    <router-link :to="{ name: 'Works Detail', params: { slug: work.slug } }"
                                                        class="view-video-btn">
                                                        View Detail
                                                    </router-link>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                            </div>
                        </div>
                       
                        
                    </div>



                    <div class="d-flex justify-content-center justify-content-lg-center mt-5">
                        <router-link :to="{ name: 'Our Works' }" class="btn btn-lg btn-secondary rounded-pill me-2"
                            style="color: #820358">
                            View All Our Works
                        </router-link>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<style scoped>
/* VIDEO */
.video-wrapper {
    max-width: 800px;
    width: 100%;
    margin: 0 auto;
    aspect-ratio: 16 / 9;
    border-radius: 10px;
    overflow: hidden;
}

.player {
    width: 100%;
    height: 100%;
    border-radius: 10px;
}

/* HTML5 Video Player */
.player:where(video) {
    object-fit: cover;
}

/* Iframe Players (YouTube, Google Drive) */
.player:where(.youtube-player, .gdrive-player) {
    position: relative;
}

.youtube-player iframe,
.gdrive-player iframe {
    width: 100%;
    height: 100%;
    border-radius: 10px;
}

.onedrive-player iframe {
    width: 100%;
    height: 100%;
    border-radius: 10px;
}

/* Poster Overlay */
.poster-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
    background: #000;
}

.poster-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0.8;
    transition: opacity 0.3s ease;
}

.poster-overlay:hover .poster-image {
    opacity: 0.6;
}

.play-button {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 68px;
    height: 48px;
    transition: transform 0.2s ease;
}

.poster-overlay:hover .play-button {
    transform: translate(-50%, -50%) scale(1.1);
}

.play-button svg {
    width: 100%;
    height: 100%;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
}

/* IMAGE FIT */
.services-card {
    position: relative;
    overflow: hidden;
    border-radius: 14px;
    background: #000;
    height: 100%;
}

.services-image {
    height: 100%;
}

.services-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform .4s ease;
}

/* GRADIENT OVERLAY */
.team-info {
    position: absolute;
    inset: 0;
    padding: 35px;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    background: linear-gradient(180deg,
            rgba(130, 3, 88, 0) 55%,
            rgba(63, 1, 48, 1) 100%);
}

/* CONTENT */
.team-content {
    text-align: center;
    transform: translateY(60px);
    transition: transform .35s ease;
}

.team-name {
    display: block;
    color: #fff;
    font-weight: 600;
    font-size: 1.05rem;
}

.view-video-btn {
    display: inline-block;
    margin-top: 8px;
    font-size: .9rem;
    color: #ffae0b;
    text-decoration: none;
    opacity: 0;
    transform: translateY(10px);
    transition: all .3s ease;
}

/* HOVER */
.services-card:hover img {
    transform: scale(1.06);
}

.services-card:hover .team-content {
    transform: translateY(0);
}

.services-card:hover .view-video-btn {
    opacity: 1;
    transform: translateY(0);
}
</style>
